import React from 'react';
import { Type, Stamp, Position } from 'lucide-react';

export type WatermarkPosition = 'top-left' | 'top-right' | 'center' | 'bottom-left' | 'bottom-right';

export interface WatermarkState {
  enabled: boolean;
  text: string;
  color: string;
  fontSize: number; // in px
  opacity: number; // 0 - 100
  position: WatermarkPosition;
}

export const INITIAL_WATERMARK: WatermarkState = {
  enabled: false,
  text: '© My Watermark',
  color: '#ffffff',
  fontSize: 32,
  opacity: 70,
  position: 'bottom-right',
};

interface WatermarkEditorProps {
  watermark: WatermarkState;
  onChange: (watermark: WatermarkState) => void;
}

export function WatermarkEditor({ watermark, onChange }: WatermarkEditorProps) {
  const updateProp = <K extends keyof WatermarkState>(key: K, value: WatermarkState[K]) => {
    onChange({ ...watermark, [key]: value });
  };

  const positions: { id: WatermarkPosition; label: string }[] = [
    { id: 'top-left', label: 'TL' },
    { id: 'top-right', label: 'TR' },
    { id: 'center', label: 'Center' },
    { id: 'bottom-left', label: 'BL' },
    { id: 'bottom-right', label: 'BR' },
  ];

  return (
    <div className="space-y-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
      <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800">
        <label className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={watermark.enabled}
            onChange={(e) => updateProp('enabled', e.target.checked)}
            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
          />
          <Stamp className="w-4 h-4 text-indigo-500" />
          Add Text Watermark Overlay
        </label>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${watermark.enabled ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>
          {watermark.enabled ? 'Active' : 'Disabled'}
        </span>
      </div>

      {watermark.enabled && (
        <div className="space-y-3 pt-1">
          {/* Text input */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Watermark Text</label>
            <div className="flex rounded-lg border border-slate-300 dark:border-slate-700 overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500">
              <span className="inline-flex items-center px-3 bg-slate-100 dark:bg-slate-800 text-slate-500">
                <Type className="w-3.5 h-3.5" />
              </span>
              <input
                type="text"
                value={watermark.text}
                onChange={(e) => updateProp('text', e.target.value)}
                placeholder="Enter watermark text..."
                className="w-full px-3 py-1.5 text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-none focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Position Selector Grid */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Position</label>
              <div className="grid grid-cols-3 gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg text-center">
                {positions.map((pos) => (
                  <button
                    key={pos.id}
                    type="button"
                    onClick={() => updateProp('position', pos.id)}
                    className={`py-1 text-[11px] font-semibold rounded transition-colors ${
                      watermark.position === pos.id
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {pos.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Font Size */}
            <div>
              <div className="flex justify-between text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                <span>Font Size</span>
                <span className="font-mono">{watermark.fontSize}px</span>
              </div>
              <input
                type="range"
                min="12"
                max="96"
                value={watermark.fontSize}
                onChange={(e) => updateProp('fontSize', Number(e.target.value))}
                className="w-full h-1.5 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
            </div>

            {/* Opacity */}
            <div>
              <div className="flex justify-between text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                <span>Opacity</span>
                <span className="font-mono">{watermark.opacity}%</span>
              </div>
              <input
                type="range"
                min="10"
                max="100"
                value={watermark.opacity}
                onChange={(e) => updateProp('opacity', Number(e.target.value))}
                className="w-full h-1.5 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
